import { IonButton, IonCheckbox, IonCol, IonGrid, IonItem, IonLabel, IonRow } from "@ionic/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import FilterList, { isFilterActive } from "../../../FilterList/FilterList";

import { useL10n } from "@ews/react-localization-context";

import List from "../../../List/List";
import { defaultPageRange } from "../../../List/Pagination/pagerange";

import { PaginatedResult, PaginationRequest, permissionAllowed, RpcSendListRequestExportableList, SystemMeasurements, SystemOperate } from "@ews/react-data";
import { formatLogicalAddress, loopType, NumberGroup } from "@ews/zlt-events";
import { Locale } from "../../../../setup/locale";
import { FilterState } from "../../../FilterList/types";
import { HeaderOption } from "../../../List/types";
import { useMyDateFormat, useMyPermissions, useMyProfile } from "../../../Login/LoginProvider";
import { useNamedRoutes } from "../../../NamedRoutes";
import { usePersistentState } from "../../../Persistence";
import { useInvokeSendList } from "../../../ReactData/rpc";
import { useFetchSystemMeasurement, useOperateSystem, useSystemMaintenance } from "../../../ReactData/system";
import { useIsMobile } from "../../../Responsive";
import DatePicker, { dateRange, dateStringToValue, valueToDateString } from "../../../Settings/SettingsComponents/SettingsRangeInput/components/DatePicker/DatePicker";
import SettingsZoneRangeInput, { covertZoneStringToObject, formatElement } from "../../../Settings/SettingsComponents/SettingsRangeInput/components/Zone/SettingsZoneRangeInput";
import { T_GROUP_LIST } from "../../SystemSettings/types";
import { Filter } from "../types/reportTypes";
import analogueStyle from "./AnalogueValues.module.scss";
import type { AnalogueFilterKeys, AnalogueValuesProps } from "./types";



type PersistenceFilter = Filter<AnalogueFilterKeys> & FilterState;
const defaultRange = dateRange(new Date(), 1);

const hasValidValue = (value: number | undefined) => value && value < 0xff ? value : "...";

const ignoreKeys: (keyof PersistenceFilter)[] = ["pageSize", "isFilterOpen", "page", "pageSize", "sortCriteria", "sortOrder"];

const AnalogueValues: React.FC<AnalogueValuesProps> = ({
    system,
    filter,
    onIsOpen,
    onFilterActive,

}) =>
{
    const invokeSendList = useInvokeSendList();
    const filterData = useFetchSystemMeasurement();
    const { translate: t } = useL10n();
    const dateFormat = useMyDateFormat();
    const maintenanceSettings = useSystemMaintenance(system.id || "");
    const mobileView = useIsMobile();
    const operate = useOperateSystem();
    const me = useMyProfile();
    const [refresh, setRefresh] = useState<boolean>(true);

    const loadMeasurements = useCallback(async () =>
    {
        const { id } = system;
        if (!id) return;
        await operate(id, { command: 'MEASURE' } as SystemOperate);
    }, [operate, system]);

    const permissions = useMyPermissions();
    const { params } = useNamedRoutes();
    const { numberGroup, element, group } = params();
    const loadMeasurementsButton = useRef<HTMLIonButtonElement>(null);

    const standardFilter: Filter<AnalogueFilterKeys> = useMemo(() => ({
        numberGroup: [NumberGroup.Zone, NumberGroup.ActuationDevice, NumberGroup.AlarmingDevice].sort().join(","),
        created: valueToDateString(defaultRange, system.timezone),
        sortCriteria: "created",
        sortOrder: "DESC",
        pageSize: defaultPageRange
    }), [
        system.timezone,
    ]);

    const [result, setResult] = useState<PaginatedResult<SystemMeasurements>>({
        currentPage: 0,
        pageSize: standardFilter.pageSize || defaultPageRange,
        totalCountOfEntries: 0,
        totalCountOfPages: 0,
        results: [],
    });


    const [details, showDetails] = useState(false);
    const [currentState, setState] = usePersistentState<PersistenceFilter>('systems-analoguevalues:filter', { ...standardFilter });

    const sortCriteria = useRef<{
        key: string;
        direction: "ASC" | "DESC";
    } | undefined>(undefined);

    const getFilteredData = useCallback(async (id: string, filter: PaginationRequest & AnalogueFilterKeys) =>
    {
        const response = await filterData(id, filter);
        if (!response) return;

        setResult(response);
    }, [filterData]);

    const onPageChange = useCallback((page: PersistenceFilter["page"]) => setState((prev) => ({ ...prev, page })), [setState]);
    const onSortChange = useCallback((sortCriteria: PersistenceFilter["sortCriteria"], sortOrder: PersistenceFilter["sortOrder"]) => setState((prev) => ({ ...prev, sortCriteria, sortOrder })), [setState]);
    const onPageSizeChange = useCallback((pageSize: PersistenceFilter["pageSize"]) => setState((prev) => ({ ...prev, pageSize })), [setState]);
    const onReloadChange = useCallback(() => setState((prev) => ({ ...prev })), [setState]);
    const onIsFilterOpen = useCallback((state: boolean) => { onIsOpen && onIsOpen(state, "main"); }, [onIsOpen]);
    const onResetFilter = useCallback(() => setState(() => standardFilter), [setState, standardFilter]);
    const isFilterChanged = isFilterActive<PersistenceFilter>(currentState, standardFilter, ignoreKeys);

    useEffect(() =>
    {
        if (filter?.isFilterActive !== isFilterChanged) {
            onFilterActive && onFilterActive(isFilterChanged);
        }
    }, [isFilterChanged, filter, onFilterActive]);


    const header: HeaderOption<Locale, SystemMeasurements>[] = useMemo(() => [
        {
            title: 'TD_ZEIT',
            key: 'created',
            className: `${analogueStyle["col"]} ${mobileView ? analogueStyle['sizeFull'] : analogueStyle["size3"]}`,
            both: true,
            child: ({ created }: SystemMeasurements) =>
            {
                return <>
                    {dateFormat(new Date(created!), system.timezone)}
                </>;
            }
        },
        {
            title: '[SID_ELEMENT_DEV]',
            key: 'zone,element',
            className: `${analogueStyle["col"]} ${mobileView ? analogueStyle['sizeFull'] : `${analogueStyle["size3"]} ${analogueStyle["nowrap"]}`}`,
            both: true,
            child: ({ zone, element, numberGroup }: SystemMeasurements) =>
            {
                const T_GROUP = T_GROUP_LIST[(numberGroup || NumberGroup.Zone) as keyof typeof T_GROUP_LIST] || "";

                return zone && element ? <>{t(T_GROUP)} {`${formatLogicalAddress(zone, element, numberGroup || NumberGroup.Zone, maintenanceSettings.formatLeadingZeros)}`}</> : null;
            }
        },
        ...(details ? [5, 4, 3, 2, 1, 0].map(n =>
        {
            return {
                title: `-${n + 1}MNTH`,
                key: `avg${n}`,
                className: `${analogueStyle["col"]} ${mobileView ? analogueStyle['sizeFull'] : analogueStyle["size1_5"]}`,
                both: (true as true),
                child: ((row: SystemMeasurements) =>
                {
                    const value = row[`avg${n}` as keyof SystemMeasurements];
                    const month = row[`month${n}` as keyof SystemMeasurements];
                    const year = row[`year${n}` as keyof SystemMeasurements];
                    return value ? <>{`${month}`.padStart(2, "0")}.{year}: {value}</> : <></>;
                })
            } as any;
        })
            :
            []
        ),
        {
            title: 'COM_FACPMANAGEMENT_LOGS_MEASUREMENTS_ACTUAL_VALUE',
            key: 'analogueValue',
            className: `${analogueStyle["col"]} ${mobileView ? analogueStyle['sizeFull'] : analogueStyle["size1_2"]}`,
            both: true,
            child: ({ analogueValue }: SystemMeasurements) =>
            {
                const _value = hasValidValue(analogueValue);
                return mobileView ? `${t("COM_FACPMANAGEMENT_LOGS_MEASUREMENTS_ACTUAL_VALUE")}: ${_value}` : _value;
            }
        },
        {
            title: 'COM_FACPMANAGEMENT_LOGS_MEASUREMENTS_ALARM_VALUE',
            key: 'alarmThreshold',
            className: `${analogueStyle["col"]} ${mobileView ? analogueStyle['sizeFull'] : analogueStyle["size1_2"]}`,
            both: true,
            child: ({ alarmThreshold }: SystemMeasurements) =>
            {
                const _value = hasValidValue(alarmThreshold);
                return mobileView ? `${t("COM_FACPMANAGEMENT_LOGS_MEASUREMENTS_ALARM_VALUE")}: ${_value}` : _value;
            }
        },
        {
            title: 'COM_FACPMANAGEMENT_LOGS_MEASUREMENTS_DIRT',
            key: 'value',
            className: `${analogueStyle["col"]} ${mobileView ? analogueStyle['sizeFull'] : analogueStyle["size1_2"]}`,
            both: true,
            child: ({ value }: SystemMeasurements) =>
            {
                const _value = hasValidValue(value);
                return mobileView ? `${t("COM_FACPMANAGEMENT_LOGS_MEASUREMENTS_DIRT")}: ${_value}` : _value;
            }
        },
        {
            title: 'COM_FACPMANAGEMENT_LOGS_MEASUREMENTS_DIRT_12MONTHS',
            key: 'prediction12m',
            className: `${analogueStyle["col"]} ${mobileView ? analogueStyle['sizeFull'] : analogueStyle["size1_2"]}`,
            both: true,
            child: ({ prediction12m }: SystemMeasurements) =>
            {
                const _value = hasValidValue(prediction12m);
                return mobileView ? `${t("COM_FACPMANAGEMENT_LOGS_MEASUREMENTS_DIRT_12MONTHS")}: ${_value}` : _value;
            }
        },
        {
            title: 'COM_FACPMANAGEMENT_LOGS_MEASUREMENTS_MONTHS_TO_CHANGE',
            key: 'monthsLeft',
            className: `${analogueStyle["col"]} ${mobileView ? analogueStyle['sizeFull'] : analogueStyle["size1_2"]}`,
            both: true,
            child: ({ monthsLeft }: SystemMeasurements) =>
            {
                const value = hasValidValue(monthsLeft);
                let _value = undefined;

                if (value === "...") {
                    _value = value;
                } else {
                    _value = value > 12 ? ">12" : value;
                }
                return mobileView ? `${t("COM_FACPMANAGEMENT_LOGS_MEASUREMENTS_MONTHS_TO_CHANGE")}: ${_value}` : _value;
            }
        },
        {
            title: 'TD_ELEMENTTYP',
            key: 'elementType',
            both: true,
            child: ({ elementType }: SystemMeasurements) => elementType ? <>{t(loopType(elementType))}</> : <></>
        },
        {
            title: '[SID_DLG_TXT_TEXT2]',
            key: 'text',
            both: true,
        },
    ], [dateFormat, details, maintenanceSettings.formatLeadingZeros, mobileView, system.timezone, t]);

    useEffect(() =>
    {
        sortCriteria.current =
            currentState.sortCriteria ? {
                key: currentState.sortCriteria,
                direction: currentState.sortOrder || "ASC"
            } : undefined;

    }, [currentState.sortCriteria, currentState.sortOrder]);

    function onClickSendList()
    {
        if (!system?.id) {
            console.error("onClickSendList: system.id is undefined. This should not be possible. Calling sendList rpc skipped.");
            return;
        }

        console.debug("onClickSendList: invoking sendList rpc with filter", currentState);

        invokeSendList({
            apiParams: {
                systemId: system.id
            } as any,
            filter: currentState as any,
            list: RpcSendListRequestExportableList.SystemMeasurements,
        });
    }

    useEffect(() =>
    {
        if (group) {

            setState((current) =>
            {
                return {
                    ...current,
                    sortCriteria: "created",
                    sortOrder: "DESC",
                    numberGroup,
                    zone: `${group}/${formatElement(element || "001")},${group}/${formatElement(element || "255")}`,
                    created: valueToDateString(dateRange(new Date(), 1), system.timezone)
                };
            });

            loadMeasurements();
            setRefresh(true);
        }
    }, [
        numberGroup,
        element,
        group,
        loadMeasurements,
        setState,
        system.timezone
    ]);

    useEffect(() =>
    {
        const { id } = system;
        if (!id) return;

        if (group && currentState.zone !== `${group}/${formatElement(element || "001")},${group}/${formatElement(element || "255")}`) {
            return;
        }

        getFilteredData(id, currentState);

    }, [
        currentState,
        getFilteredData,
        system,
        group,
        element
    ]);

    const numberGroupIsChecked = useCallback((numberGroup: NumberGroup) =>
    {
        const selectedGroups = (currentState.numberGroup || "").split(",").filter(v => v).map(v => parseInt(v));
        return selectedGroups.indexOf(numberGroup) >= 0;
    }, [currentState.numberGroup]);

    const handleNumberGroup = useCallback((numberGroup: NumberGroup, checked: boolean) =>
    {
        const newState = { ...currentState };
        const selectedGroups = (newState.numberGroup || "").split(",").filter(v => v).map(v => parseInt(v));
        const index = selectedGroups.indexOf(numberGroup);

        if (index >= 0) selectedGroups.splice(index, 1);
        if (checked) selectedGroups.push(numberGroup);

        if (selectedGroups.length) {
            newState.numberGroup = selectedGroups.filter(numberGroup => numberGroup !== NumberGroup.Undefined).sort().join(",");
        } else {
            newState.numberGroup = `${NumberGroup.Undefined}`;
        }

        setState(newState);
    }, [currentState, setState]);


    useEffect(() =>
    {
        let interval: number;
        if (refresh) {
            interval = window.setInterval(() => setState((current) => ({ ...current })), 5000);
        };

        return () => window.clearInterval(interval);

    }, [refresh, setState]);

    const isAllowedMeasurements = permissionAllowed(permissions.permitAccessLevel3!) || (Boolean(permissions.detectorValuesAccessLevel2) && permissionAllowed(permissions.permitAccessLevel2!));
    if (!isAllowedMeasurements) return <></>;

    const checkboxWidth = { minWidth: "100%" };

    return <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <FilterList
            onReset={onResetFilter}
            onIsOpen={onIsFilterOpen}
            isOpen={filter?.isFilterOpen || false}
            noPrint
        >
            <IonCol>
                <IonGrid style={{ overflowY: "auto" }}>
                    <IonRow>
                        <IonCol style={{ border: "0" }}>
                            <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                                <IonLabel style={{ maxWidth: "fit-content" }}>{t("TD_FILTER")}</IonLabel>
                            </div>
                        </IonCol>
                    </IonRow>
                    <IonRow>
                        {/* <IonCol size="12" style={{ display: "flex", justifyContent: "start", alignItem: "center", border: "0", ...checkboxWidth }} >

                                <IonCheckbox
                                    style={{ margin: 0, padding: 0, width: "fit-content" }}

                                    onIonChange={({ detail }) =>
                                    {
                                        const checked = detail.checked;
                                        console.log(detail);
                                    }}>
                                    {" "}
                                </IonCheckbox>
                                <IonLabel style={{ margin: "0 10px", verticalAlign: "middle" }}>{t('TD_GROSS_ALLE')}</IonLabel>
                            </IonCol> */}
                        <IonCol size="12" style={{ display: "flex", justifyContent: "start", alignItem: "center", border: "0", ...checkboxWidth }} >

                            <IonCheckbox
                                checked={numberGroupIsChecked(NumberGroup.Zone)}
                                style={{ margin: 0, padding: 0, width: "fit-content" }}
                                onIonChange={({ detail }) => handleNumberGroup(NumberGroup.Zone, detail.checked)}
                            >
                                {" "}
                            </IonCheckbox>
                            <IonLabel style={{ margin: "0 10px", verticalAlign: "middle" }}>{t('[SID_MNU_TAB_GROUPS]')}</IonLabel>
                        </IonCol>
                        <IonCol size="12" style={{ display: "flex", justifyContent: "start", alignItem: "center", border: "0" }} >

                            <IonCheckbox
                                checked={numberGroupIsChecked(NumberGroup.ActuationDevice)}
                                style={{ margin: 0, padding: 0, width: "fit-content" }}
                                onIonChange={({ detail }) => handleNumberGroup(NumberGroup.ActuationDevice, detail.checked)}
                            >
                                {" "}
                            </IonCheckbox>
                            <IonLabel style={{ margin: "0 10px", verticalAlign: "middle" }}>{t('[SID_MNU_TAB_CONTROLS]')}</IonLabel>
                        </IonCol>
                        <IonCol size="12" style={{ display: "flex", justifyContent: "start", alignItem: "center", border: "0" }} >

                            <IonCheckbox
                                checked={numberGroupIsChecked(NumberGroup.AlarmingDevice)}
                                style={{ margin: 0, padding: 0, width: "fit-content" }}
                                onIonChange={({ detail }) => handleNumberGroup(NumberGroup.AlarmingDevice, detail.checked)}
                            >
                                {" "}
                            </IonCheckbox>
                            <IonLabel style={{ margin: "0 10px", verticalAlign: "middle" }}>{t('[SID_MNU_TAB_AL_DEVICES]')}</IonLabel>
                        </IonCol>



                    </IonRow>
                </IonGrid>
            </IonCol>
            <IonCol size="3">
                <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                    <IonLabel style={{ maxWidth: "fit-content" }}>{t("TD_DATUM_DRUCK")}</IonLabel>
                </div>
                <DatePicker
                    className={analogueStyle.dateTime}
                    childText={{ start: "[REACT_START]", end: "COM_FACPMANAGEMENT_END_EVENT" }}
                    value={dateStringToValue(currentState.created!)}
                    divider={false}
                    position="row"
                    onChange={(range) =>
                    {
                        const newState = { ...currentState };
                        newState.created = valueToDateString(range, system.timezone);
                        setState(newState);
                    }}
                />
            </IonCol>
            <IonCol sizeLg="3" sizeMd="1" sizeSm="1">
                <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                    <IonLabel style={{ maxWidth: "fit-content" }}>{t("TD_ELEMENTTYP")}</IonLabel>
                </div>
                <SettingsZoneRangeInput
                    lines="none"
                    divider={false}
                    labels={{ start: "[SID_FROM]", end: "[SID_TO]" }}
                    inputLabels={{ parent: "[REACT_NUMBER]", children: "[SID_ELEMENT_DEV]" }}
                    position={{ parent: "row", children: "line" }}
                    value={covertZoneStringToObject(currentState.zone) || {}}
                    reset={false}
                    onChange={(value) =>
                    {
                        const newState = { ...currentState };
                        newState.zone = `${value.startZoneElement}`;

                        if (value.endZoneElement) {
                            newState.zone = `${value.startZoneElement},${value.endZoneElement}`;
                        }
                        setState(newState);
                    }}
                />
            </IonCol>
            <IonCol style={{ display: 'flex', flexDirection: "column", alignItems: "end" }}>
                <IonRow >
                    <IonButton
                        ref={loadMeasurementsButton}
                        color={"success"}
                        onClick={() =>
                        {
                            loadMeasurements();
                            setState({ ...currentState });
                        }}
                    >
                        <span> {`${t("[REACT_LOAD_MEASUREMENTS]")}`}</span>
                    </IonButton>
                </IonRow>
                <IonRow >
                    <IonButton
                        color={"success"}
                        onClick={onClickSendList}
                    >
                        <span>{t("[REACT_WERTE_VERSENDEN]")}</span>
                    </IonButton>
                </IonRow>
                <IonItem style={{ flexGrow: 1, alignContent: "end" }} lines="none">
                    <IonCheckbox
                        style={{ margin: 0, padding: 0, width: "fit-content" }}

                        onIonChange={({ detail }) =>
                        {
                            showDetails(detail.checked);
                        }}>
                        {" "}
                    </IonCheckbox>
                    <IonLabel style={{ margin: "0 10px", verticalAlign: "middle", width: "fit-content" }}>{t('[REACT_SHOW_HISTORY]')}</IonLabel>
                </IonItem>
                <IonItem>
                    <IonCheckbox
                        checked={refresh}
                        style={{ margin: 0, padding: 0, width: "fit-content" }}
                        onIonChange={({ detail }) => setRefresh(detail.checked)}
                    >
                        {" "}
                    </IonCheckbox>
                    <IonLabel style={{ margin: "0 10px", verticalAlign: "middle" }}>{t('[REACT_AUTO_REFRESH_LIST]')}</IonLabel>
                </IonItem>

            </IonCol>
        </FilterList >

        <List
            rows={result.results}
            sort={sortCriteria.current}
            pageSize={result.pageSize}
            onSort={onSortChange}
            onPageChange={onPageChange}
            onPageSize={onPageSizeChange}
            onReload={onReloadChange}
            currentPage={result.currentPage}
            numberOfPages={result.totalCountOfPages}
            headers={header}
        />
    </div >;
};

export default AnalogueValues;
